/**
 * RecordModal Component
 * 
 * Modal dialog for viewing and editing individual records.
 * Displays record details, similar records, and allows editing/actions.
 */

import React, { useEffect, useState } from 'react';
import { api } from '../../lib/rpc';
import './Cases.css';

/**
 * Parse content string into structured fields
 */
const parseContent = (content) => {
  if (!content) return { theme: '', question: '', answer: '' };
  const keywords = ["Тема:", "Вопрос:", "Ответ:"];
  const parts = { theme: '', question: '', answer: '' };
  const keyMap = { "Тема:": "theme", "Вопрос:": "question", "Ответ:": "answer" };
  
  const foundKeywords = keywords
    .map(k => ({ keyword: k, index: content.indexOf(k) }))
    .filter(item => item.index !== -1)
    .sort((a, b) => a.index - b.index);
  
  if (foundKeywords.length === 0) {
    return { theme: '', question: '', answer: content.trim() };
  }
  
  for (let i = 0; i < foundKeywords.length; i++) {
    const current = foundKeywords[i];
    const next = foundKeywords[i + 1];
    const start = current.index + current.keyword.length;
    const end = next ? next.index : content.length;
    const text = content.substring(start, end).trim().replace(/;$/, '').trim();
    const fieldName = keyMap[current.keyword];
    if (fieldName) parts[fieldName] = text;
  }
  return parts;
};

/**
 * Format fields back into content string
 */
const formatContent = (theme, question, answer) => {
  const parts = [];
  if (theme) parts.push(`Тема: ${theme}`);
  if (question) parts.push(`Вопрос: ${question}`);
  if (answer) parts.push(`Ответ: ${answer}`);
  return parts.join('; ') + (parts.length > 0 ? ';' : '');
};

const MAX_MODAL_DEPTH = 5;

function RecordModalView({ id, onChanged, onClose, depth, setModalContent }) {
  const [neighbors, setNeighbors] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [theme, setTheme] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [originalState, setOriginalState] = useState({ theme: '', question: '', answer: '' });

  const [confirmingDuplicate, setConfirmingDuplicate] = useState(null);
  const [expandedNeighbors, setExpandedNeighbors] = useState({});

  async function load() {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const [recordData, similarData] = await Promise.all([
        api.getCluster(id).then(res => res?.[0]),
        api.getSimilarPairs({ id })
      ]);
      const parsed = parseContent(recordData?.content);
      setTheme(parsed.theme);
      setQuestion(parsed.question);
      setAnswer(parsed.answer);
      setOriginalState({ theme: parsed.theme, question: parsed.question, answer: parsed.answer });
      setNeighbors(similarData);
    } catch (e) {
      setError(String(e.message ?? e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [id]);

  async function handleSave() {
    setBusy(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const newContent = formatContent(theme, question, answer);
      await api.editRecord({ id, newContent, metadataPatch: { edited: true } });
      setSuccessMessage('Сохранено');
      setEditing(false);
      setOriginalState({ theme, question, answer });
      onChanged();
    } catch (e) {
      setError(String(e.message ?? e));
    } finally {
      setBusy(false);
    }
  }

  function handleCancelEdit() {
    setEditing(false);
    setTheme(originalState.theme);
    setQuestion(originalState.question);
    setAnswer(originalState.answer);
  }

  async function handleSendOk() {
    setBusy(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await api.sendOk({ 
        id, 
        archiveSource: true, 
        allowArchived: false, 
        metadataExtra: {}, 
        dedupeByContent: false 
      });
      setSuccessMessage(`Отправлено в telegram (id=${res?.[0]?.telegram_id ?? '—'})`);
      onChanged();
      setTimeout(onClose, 1000);
    } catch (e) {
      setError(String(e.message ?? e));
    } finally {
      setBusy(false);
    }
  }

  async function handleNotOk() {
    setBusy(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await api.setNotOk(id, 'manual');
      setSuccessMessage('Запись заархивирована');
      onChanged();
      setTimeout(onClose, 1000);
    } catch (e) {
      setError(String(e.message ?? e));
    } finally {
      setBusy(false);
    }
  }

  function openNeighbor(neighborId) {
    if (depth > MAX_MODAL_DEPTH) {
      setError("Достигнута максимальная глубина вложенности модальных окон.");
      return;
    }
    setModalContent(
      <RecordModalView 
        id={neighborId} 
        onChanged={onChanged} 
        onClose={onClose} 
        depth={depth + 1} 
        setModalContent={setModalContent} 
      />
    );
  }

  async function executeSetDuplicate(duplicateId) {
    setBusy(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await api.setCanonical({ duplicateId, canonicalId: id });
      setSuccessMessage(`Кейс #${duplicateId} успешно помечен как дубль.`);
      const similarData = await api.getSimilarPairs({ id });
      setNeighbors(similarData);
      onChanged();
    } catch (e) {
      setError(String(e.message ?? e));
    } finally {
      setBusy(false);
      setConfirmingDuplicate(null);
    }
  }

  function toggleNeighborExpand(neighborId) {
    setExpandedNeighbors(prev => ({
      ...prev,
      [neighborId]: !prev[neighborId]
    }));
  }

  return (
    <>
      <div className="record-modal-header">
        <h3 className="record-modal-title">Запись #{id}</h3>
      </div>

      {error && <div className="alert-error">{error}</div>}
      {successMessage && <div className="alert-success">{successMessage}</div>}
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>Загрузка…</div>
      ) : (
        <>
          <div style={{ marginBottom: '16px' }}>
            <div className="record-field">
              <label>Тема</label>
              <textarea 
                value={theme} 
                readOnly={!editing} 
                onChange={(e) => setTheme(e.target.value)} 
                style={{ minHeight: '40px' }}
              />
            </div>
            <div className="record-field">
              <label>Вопрос</label>
              <textarea 
                value={question} 
                readOnly={!editing} 
                onChange={(e) => setQuestion(e.target.value)} 
                style={{ minHeight: '80px' }}
              />
            </div>
            <div className="record-field">
              <label>Ответ</label>
              <textarea 
                value={answer} 
                readOnly={!editing} 
                onChange={(e) => setAnswer(e.target.value)} 
                style={{ minHeight: '150px' }}
              />
            </div>
          </div>

          {!editing ? (
            <button 
              onClick={() => setEditing(true)} 
              className="btn btn-primary"
              style={{ marginTop: '8px' }}
            >
              Редактировать
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button 
                disabled={busy} 
                onClick={handleSave} 
                className="btn btn-success"
              >
                Сохранить
              </button>
              <button 
                disabled={busy} 
                onClick={handleCancelEdit} 
                className="btn btn-secondary"
              >
                Отмена
              </button>
            </div>
          )}

          <div className="similar-records">
            <h4>Похожие по смыслу</h4>
            {(!neighbors || neighbors.length === 0) ? (
              <p style={{ color: '#9ca3af' }}>Похожих записей не найдено.</p>
            ) : (
              <div>
                {neighbors.map(n => (
                  <div key={n.neighbor_id} className="similar-record-item">
                    <div 
                      className="similar-record-summary"
                      onClick={() => toggleNeighborExpand(n.neighbor_id)}
                    >
                      <div style={{ fontWeight: 500 }}>Кейс #{n.neighbor_id}</div>
                      <div style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                        <span style={{ marginRight: '16px' }}>Сходство: {n.sim.toFixed(3)}</span>
                        <span>
                          Статус: {n.neighbor_archived 
                            ? `Архив (канон: ${n.neighbor_canonical_id ?? '—'})` 
                            : 'Активен'
                          }
                        </span>
                      </div>
                    </div>
                    {expandedNeighbors[n.neighbor_id] && (
                      <div className="similar-record-details">
                        <pre>{n.neighbor_content}</pre>
                        <div className="similar-record-actions">
                          <button 
                            onClick={() => openNeighbor(n.neighbor_id)} 
                            className="btn btn-primary"
                            style={{ fontSize: '0.875rem', padding: '4px 12px' }}
                          >
                            Открыть
                          </button>
                          {!n.neighbor_archived && (
                            <button 
                              disabled={busy} 
                              onClick={() => setConfirmingDuplicate(n.neighbor_id)} 
                              className="btn btn-warning"
                              style={{ fontSize: '0.875rem', padding: '4px 12px' }}
                            >
                              Сделать дублем кейса #{id}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '16px', marginTop: '24px' }}>
            <button 
              disabled={busy} 
              onClick={handleSendOk} 
              className="btn btn-success"
            >
              Ок (в telegram)
            </button>
            <button 
              disabled={busy} 
              onClick={handleNotOk} 
              className="btn btn-danger"
            >
              неОк (в архив)
            </button>
          </div>
        </>
      )}

      {confirmingDuplicate !== null && (
        <div className="confirm-dialog-overlay">
          <div className="confirm-dialog">
            <h4 className="confirm-dialog-title">Подтвердите действие</h4>
            <p className="confirm-dialog-description">
              Вы уверены, что хотите сделать кейс #{confirmingDuplicate} дублем для текущего кейса #{id}? 
              Это действие нельзя будет отменить.
            </p>
            <div className="confirm-dialog-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => setConfirmingDuplicate(null)}
              >
                Отмена
              </button>
              <button 
                className="btn btn-danger" 
                onClick={() => executeSetDuplicate(confirmingDuplicate)}
              >
                Продолжить
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function RecordModal({ id, onClose, onChanged }) {
  const [modalContent, setModalContent] = useState(null);

  useEffect(() => {
    setModalContent(
      <RecordModalView 
        id={id} 
        onChanged={onChanged} 
        onClose={onClose} 
        depth={1} 
        setModalContent={setModalContent} 
      />
    );
  }, [id, onChanged, onClose]);

  return (
    <div className="record-modal-overlay">
      <div className="record-modal">
        <button onClick={onClose} className="record-modal-close">&times;</button>
        {modalContent}
      </div>
    </div>
  );
}
